import React from 'react';
import { bool, number, string, func, node } from 'prop-types';
import { AmenityIconsMap, Colors } from '@airasia-common/libraries/constants';
import { getAmenityName } from '@airasia-common/libraries/utilities/AmenityHelper';
import { translate } from '@airasia-common/libraries/i18n/LocaleProvider';
import styled from 'styled-components';
import { CaptionText, BodyText } from '../Text/Heading';
import Row from '../Container/Row';
import Wrapper from '../Wrapper';
import useWindowSize from '../../utils/windowSize';
import PopoverTag from '../PopoverTag';
import * as ICONS from './MaterialIcon';

const VerticalLine = styled.span`
  color: ${({ theme }) => theme.colors.greyLight};
  margin-left: 12px;
  margin-right: 12px;
`;

const AllAmenitiesWrapper = styled(Wrapper)`
  max-height: 280px;
  overflow-x: hidden;
  overflow-y: auto;
  display: flex;
  flex-wrap: wrap;
`;

const AmenityIcon = ({ displayId, size }) => {
  const contentId = displayId.split(AmenityIconsMap.split.level)[1];
  if (contentId && !contentId.includes('icon-')) {
    const camelCaseDisplayId =
      contentId &&
      contentId
        .split(AmenityIconsMap.split.web)
        .map(word => word.charAt(0).toUpperCase() + word.slice(1))
        .join('');
    // eslint-disable-next-line import/namespace
    return ICONS[camelCaseDisplayId] || ICONS.Done;
  } else {
    return () => (
      <span style={{ fontSize: `${size}px` }} className={`${contentId}`}></span>
    );
  }
};

const Amenity = ({
  displayId,
  name,
  size,
  isVertical,
  percentWidth,
  translationKey,
  showDeviveItemSymbol,
  translateFunc = translate,
  isShowGalleryRoomDetail,
  isShowPopover,
  isPopoverItem,
  allHotelAmenitiesLabel,
  allItems,
}) => {
  const { isMobileView } = useWindowSize();
  const MdIcon = AmenityIcon({ displayId, size });
  const mainWrapperProps = {
    display: 'flex',
    alignItems: 'center',
    flexDirection: isVertical ? 'column' : 'row',
    width: isVertical ? '64px' : `${percentWidth}%`,
    mt: isVertical ? 0 : 3,
    pr: !isShowPopover && isVertical ? 3 : 0,
    minWidth: isShowPopover && isMobileView && '64px',
    mb: isPopoverItem && !isMobileView ? '16px' : 0,
  };
  const fontProps = {
    fontSize: ['12px', '14px'],
    lineHeight: 1.285,
    color: 'greyDark',
    ellipsizeMode: 'tail',
  };

  const amenityName = getAmenityName({
    amenity: { name, translationKey },
    translateFunc,
  });

  return (
    <Wrapper
      className="amenity"
      {...mainWrapperProps}
      style={{
        cursor: isShowPopover && !isMobileView ? 'pointer' : 'inherit',
      }}
    >
      {isShowPopover && !isMobileView && !isPopoverItem ? (
        <PopoverTag
          content={
            <AllAmenitiesWrapper>
              <BodyText
                style={{
                  width: '100%',
                  marginBottom: '16px',
                }}
              >
                {allHotelAmenitiesLabel}
              </BodyText>
              {allItems}
            </AllAmenitiesWrapper>
          }
          position="top"
          background={Colors.primaryWhite}
          borderRadius={4}
          left={'auto'}
          top={'inherit'}
          bottom={'150%'}
          fontSizeContent={'14px'}
          paddingContent={'16px 12px'}
          colorContent={Colors.greyDarker}
          width={775}
          right={'0'}
          rightArrow={'16px'}
          label={
            <>
              <Row color={'greyDark'} flex="0 0 auto">
                {MdIcon && (
                  <MdIcon className="icon" style={{ fontSize: size }} />
                )}
              </Row>
              <Wrapper ml="6px">
                <CaptionText
                  className="caption"
                  numberOfLines={1}
                  textAlign="left"
                  {...fontProps}
                  color={'greyDark'}
                >
                  {amenityName}
                </CaptionText>
              </Wrapper>
            </>
          }
        />
      ) : (
        <>
          <Row
            color={!isShowGalleryRoomDetail ? 'greyDark' : 'greyLight'}
            flex="0 0 auto"
          >
            {MdIcon && <MdIcon className="icon" style={{ fontSize: size }} />}
          </Row>
          {isVertical ? (
            <Wrapper mt={2}>
              <CaptionText
                className="caption"
                numberOfLines={2}
                textAlign="center"
                {...fontProps}
                color={isShowGalleryRoomDetail ? 'greyLight' : 'greyDark'}
              >
                {amenityName}
              </CaptionText>
            </Wrapper>
          ) : (
            <Wrapper ml="6px">
              <CaptionText
                className="caption"
                numberOfLines={1}
                textAlign="left"
                {...fontProps}
                color={isShowGalleryRoomDetail ? 'greyLight' : 'greyDark'}
              >
                {amenityName}
              </CaptionText>
            </Wrapper>
          )}
          {showDeviveItemSymbol && (
            <Wrapper>
              <VerticalLine>|</VerticalLine>
            </Wrapper>
          )}
        </>
      )}
    </Wrapper>
  );
};

Amenity.propTypes = {
  displayId: string,
  name: string,
  isVertical: bool,
  percentWidth: number,
  size: number,
  translationKey: string,
  translateFunc: func,
  showDeviveItemSymbol: bool,
  isShowGalleryRoomDetail: bool,
  isShowPopover: bool,
  isPopoverItem: bool,
  allHotelAmenitiesLabel: string,
  allItems: node,
};
Amenity.defaultProps = {
  displayId: 'Wifi',
  name: 'Missing amenity name',
  size: 24,
  showDeviveItemSymbol: false,
};

export default Amenity;
